import { Original, OriginalSort } from './ApiTypes';

/**
 * 同じ URL の素材動画をまとめた 1 件。
 *
 * `Original` は `/detect_video` が呼ばれるたびに作られるため、同じ動画に対して
 * ドキュメントが何件もある。一覧に出す単位は URL なので、ここでまとめて
 * 「何回使ったか」「最後に使ったのはいつか」を持たせる。
 */
export type OriginalGroup = {
  url: string;
  title: string;
  imageUrl: string;
  /** 代表のドキュメント。生成に渡すのはこれ。 */
  original: Original;
  /** 同じ URL のドキュメント数。取り込んだ回数がそのまま使用回数になる。 */
  usedCount: number;
  /** 最後に使った日時（最新の作成日）。epoch ミリ秒。 */
  lastUsedAt?: number;
  /** 最初に取り込んだ日時（最古の作成日）。epoch ミリ秒。 */
  addedAt?: number;
};

const toMillis = (epochMillis?: number) => epochMillis ?? 0;

/**
 * 素材動画の履歴を URL 単位にまとめる。
 *
 * xhamster は取得できなくなったため一覧から外す（選ばせても生成できない）。
 */
export const groupOriginals = (originals: Original[]): OriginalGroup[] => {
  const groups = new Map<string, OriginalGroup>();
  for (const original of originals) {
    if (!original.detected || original.url.includes("xhamster.com")) {
      continue;
    }
    const existing = groups.get(original.url);
    if (!existing) {
      groups.set(original.url, {
        url: original.url,
        title: original.detected.title,
        imageUrl: original.detected.imageUrl,
        original,
        usedCount: 1,
        lastUsedAt: original.createdAt,
        addedAt: original.createdAt,
      });
      continue;
    }
    existing.usedCount += 1;
    if (toMillis(original.createdAt) > toMillis(existing.lastUsedAt)) {
      existing.lastUsedAt = original.createdAt;
      // 代表は最新のドキュメントにする（サムネイル URL が失効しにくい）。
      existing.original = original;
      existing.title = original.detected.title;
      existing.imageUrl = original.detected.imageUrl;
    }
    if (toMillis(original.createdAt) < toMillis(existing.addedAt) || !existing.addedAt) {
      existing.addedAt = original.createdAt;
    }
  }
  return Array.from(groups.values());
};

export const sortOriginalGroups = (groups: OriginalGroup[], sort: OriginalSort): OriginalGroup[] => {
  const sorted = [...groups];
  if (sort === "newest") {
    return sorted.sort((a, b) => toMillis(b.addedAt) - toMillis(a.addedAt));
  }
  if (sort === "oldest") {
    return sorted.sort((a, b) => toMillis(a.addedAt) - toMillis(b.addedAt));
  }
  return sorted.sort((a, b) => toMillis(b.lastUsedAt) - toMillis(a.lastUsedAt));
};

/**
 * タイトルと URL に対する部分一致。空白区切りの語をすべて含むものだけ通す
 * （「blonde pov」で絞り込めるようにする）。
 */
export const matchesOriginalQuery = (group: OriginalGroup, text: string) => {
  const words = text.trim().toLowerCase().split(/\s+/).filter(word => word.length > 0);
  if (words.length === 0) {
    return true;
  }
  const target = `${group.title} ${group.url}`.toLowerCase();
  return words.every(word => target.includes(word));
};
